import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LaborService } from '../services/laborService';
import { LaborRate } from '../types';

export const AdminLaborRatesPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rates, setRates] = useState<LaborRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rateType: 'hourly' as 'hourly' | 'per_sqft',
    hourlyRate: '',
    perSqftRate: '',
    markupFactor: '2.0',
    isActive: true,
    order: '0',
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await LaborService.getAllLaborRates();
      setRates(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as any;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);

      const rateData = {
        name: formData.name,
        description: formData.description,
        rateType: formData.rateType,
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
        perSqftRate: formData.perSqftRate ? parseFloat(formData.perSqftRate) : undefined,
        markupFactor: parseFloat(formData.markupFactor) || 2.0,
        isActive: formData.isActive,
        order: parseInt(formData.order) || 0,
      };

      if (editingId) {
        await LaborService.updateLaborRate(editingId, rateData as any);
      } else {
        await LaborService.createLaborRate(rateData as any);
      }

      await loadData();
      resetForm();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (rate: LaborRate) => {
    setEditingId(rate.id);
    setFormData({
      name: rate.name,
      description: rate.description,
      rateType: rate.rateType,
      hourlyRate: rate.hourlyRate?.toString() || '',
      perSqftRate: rate.perSqftRate?.toString() || '',
      markupFactor: rate.markupFactor.toString(),
      isActive: rate.isActive,
      order: rate.order.toString(),
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this labor rate?')) return;
    try {
      await LaborService.deleteLaborRate(id);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      rateType: 'hourly',
      hourlyRate: '',
      perSqftRate: '',
      markupFactor: '2.0',
      isActive: true,
      order: '0',
    });
    setShowForm(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p>Loading labor rates...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>Labor Rates</h1>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          style={{
            padding: '10px 16px',
            background: 'linear-gradient(135deg, var(--header-background) 0%, var(--header-button) 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
          }}
        >
          + Add Rate
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee2e2', color: '#c33', marginBottom: '20px', borderRadius: '6px' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div style={{ background: 'white', padding: '20px', marginBottom: '24px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label>Name*</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>

              <div>
                <label>Rate Type*</label>
                <select name="rateType" value={formData.rateType} onChange={handleChange} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}>
                  <option value="hourly">Hourly</option>
                  <option value="per_sqft">Per Square Foot</option>
                </select>
              </div>

              {formData.rateType === 'hourly' && (
                <div>
                  <label>Hourly Rate ($)*</label>
                  <input type="number" name="hourlyRate" value={formData.hourlyRate} onChange={handleChange} step="0.01" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
                </div>
              )}

              {formData.rateType === 'per_sqft' && (
                <div>
                  <label>Rate Per Sqft ($)*</label>
                  <input type="number" name="perSqftRate" value={formData.perSqftRate} onChange={handleChange} step="0.01" required style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
                </div>
              )}

              <div>
                <label>Markup Factor (1.0 - 5.0)</label>
                <input type="number" name="markupFactor" value={formData.markupFactor} onChange={handleChange} min="1" max="5" step="0.1" style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }} />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label>Description</label>
              <textarea name="description" value={formData.description} onChange={handleChange} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', minHeight: '80px' }} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" style={{ padding: '10px 16px', background: 'var(--primary-button)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                {editingId ? 'Update' : 'Create'} Rate
              </button>
              <button type="button" onClick={resetForm} style={{ padding: '10px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Name</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Type</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Rate</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Markup</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={rate.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 16px' }}>{rate.name}</td>
                <td style={{ padding: '12px 16px' }}>{rate.rateType === 'hourly' ? 'Hourly' : 'Per Sqft'}</td>
                <td style={{ padding: '12px 16px' }}>
                  ${rate.hourlyRate || rate.perSqftRate || '0'}
                </td>
                <td style={{ padding: '12px 16px' }}>{((rate.markupFactor - 1) * 100).toFixed(0)}%</td>
                <td style={{ padding: '12px 16px' }}>
                  <button onClick={() => handleEdit(rate)} style={{ padding: '6px 12px', marginRight: '8px', background: '#dbeafe', color: '#1e40af', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDelete(rate.id)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
